import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import { toast } from "sonner";
import ChatPanel from "./ChatPanel";
import { FilePreviewModal } from "./chat/FilePreview";
import TaskHeader from "./panel/TaskHeader";
import ChecklistSection from "./panel/ChecklistSection";
import LinksSection from "./panel/LinksSection";
import TaskMetaSection from "./panel/TaskMetaSection";
import SubtasksSection from "./panel/SubtasksSection";
import AttachmentsSection from "./panel/AttachmentsSection";
import ActivitySection from "./panel/ActivitySection";
import { useTaskDetailActions } from "./panel/useTaskDetailActions";
import { cn } from "../lib/utils";

export default function TaskDetailPanel({ taskId, onClose }) {
  const { users } = useData();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("chat");
  const [preview, setPreview] = useState(null);

  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tasks/${taskId}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
      onCloseRef.current();
    }
  }, [taskId]);

  useEffect(() => { setData(null); load(); }, [load]);

  const { patch, deleteTask, subtaskTitle, setSubtaskTitle, addSubtask, toggleSubtask, uploading, upload, deleteAttachment } =
    useTaskDetailActions({ task: data?.task, setData, load, onClose });

  if (!data) {
    return (
      <div className="fixed inset-y-0 right-0 w-full md:w-[600px] border-l border-border bg-background shadow-2xl z-50 flex items-center justify-center" data-testid="task-panel-loading">
        <div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const { task, subtasks, activities, attachments } = data;

  const tabCls = (t) => cn("px-3 py-2 text-sm border-b-2 transition-colors",
    tab === t ? "text-foreground border-[#E31E24] font-medium" : "text-muted-foreground border-transparent hover:text-foreground");

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[600px] border-l border-border bg-background shadow-2xl z-50 flex flex-col task-row-enter" data-testid="task-detail-panel">
      <TaskHeader task={task} patch={patch} deleteTask={deleteTask} onClose={onClose} />

      <div className="flex-1 overflow-y-auto">
        <TaskMetaSection task={task} users={users} patch={patch} />

        <ChecklistSection task={task} patch={patch} />

        <div className="p-4 border-b border-border">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Description</p>
          <textarea data-testid="panel-description" defaultValue={task.description || ""} key={task.id} rows={3} placeholder="Add a description…"
            onBlur={(e) => e.target.value !== task.description && patch({ description: e.target.value })}
            className="w-full px-3 py-2 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary resize-none" />
        </div>

        <LinksSection task={task} patch={patch} />

        <SubtasksSection task={task} subtasks={subtasks} toggleSubtask={toggleSubtask}
          subtaskTitle={subtaskTitle} setSubtaskTitle={setSubtaskTitle} addSubtask={addSubtask} />

        <div className="border-b border-border flex px-2" data-testid="panel-tabs">
          <button data-testid="tab-chat" className={tabCls("chat")} onClick={() => setTab("chat")}>Chat</button>
          {user?.role !== "pelaksana" && (
            <button data-testid="tab-attachments" className={tabCls("attachments")} onClick={() => setTab("attachments")}>Files ({attachments.length})</button>
          )}
          <button data-testid="tab-activity" className={tabCls("activity")} onClick={() => setTab("activity")}>Activity</button>
        </div>

        <div className="p-4">
          {tab === "chat" && <ChatPanel channel={`task_${task.id}`} className="h-[400px]" testid="task-chat" />}
          {tab === "attachments" && user?.role !== "pelaksana" && (
            <AttachmentsSection attachments={attachments} uploading={uploading} upload={upload}
              deleteAttachment={deleteAttachment} onPreview={setPreview} />
          )}
          {tab === "activity" && <ActivitySection activities={activities} />}
        </div>
      </div>
      {preview && <FilePreviewModal file={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
